import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { type UrlParams, type UrlValidationResult, validateUrlParams } from '@/composables/url-params-parser';
import { bookingInit, getHouseholdsFullData } from '@/services/api';
import type { Address } from '@/types/address';
import type {
  BookingApproach,
  ConnectionPrice,
  PhonePrefix,
  BookingInit,
  HouseholdFullData,
} from '@/types/common';
import { BookingMode } from '@/types/order-main';
import type { User } from '@/types/user';

const useInitStore = defineStore('init', () => {
  const addressId = ref<number>();
  const booking = ref<BookingInit>();
  const error = ref<string>();
  const household = ref<HouseholdFullData>();
  const notBookableReason = ref<string>();
  const params = ref<UrlParams>();

  const hasError = computed<boolean>(() => error.value !== undefined);
  const initialBookingMode = computed<BookingMode | undefined>(
    () => params.value?.initBookingMode,
  );
  const isNotBookable = computed<boolean>(() => notBookableReason.value !== undefined);
  const paramsAddress = computed<Address | undefined>(() => {
    if (!params.value) {
      return undefined;
    }
    const {
      street, houseNumber, suffix,
      zipCode, city,
    } = params.value;
    return {
      id: addressId.value,
      street,
      houseNumber: houseNumber.toString(),
      suffix: suffix ?? undefined,
      zipCode: zipCode.toString(),
      city,
    };
  });
  const paramsAddressText = computed<string | undefined>(() => {
    if (!params.value) {
      return undefined;
    }
    const {
      street, houseNumber, suffix,
      zipCode, city,
    } = params.value;
    return `${street} ${houseNumber}${suffix ?? ''}, ${zipCode} ${city}`;
  });
  const bookingApproach = computed<BookingApproach | undefined>(
    () => booking.value?.bookingApproach,
  );
  const currentUser = computed<User | undefined>(() => booking.value?.currentUser);
  const phonePrefixes = computed<PhonePrefix[] | undefined>(() => booking.value?.phonePrefixes);
  const priceList = computed<ConnectionPrice | undefined>(() => booking.value?.priceList);
  const householdIsCompany = computed<boolean>(
    () => household.value?.household?.isCompany ?? false,
  );
  const householdHasProductBooked = computed<boolean>(
    () => household.value?.household?.hasProductBooked ?? false,
  );

  async function validateUrlQuery(isLocal = false): Promise<UrlValidationResult> {
    const validation = await validateUrlParams(isLocal);
    params.value = validation.isError ? undefined : validation.result;
    error.value = validation.isError ? validation.error : undefined;
    if (validation.isError) {
      notBookableReason.value = validation.error;
    }
    return validation;
  }

  async function loadBookingMetadata(): Promise<void> {
    if (isNotBookable.value) {
      return;
    }

    const {
      street,
      houseNumber,
      suffix,
      zipCode,
      city,
      householdId,
      customerId,
    } = params.value!;

    const [initError, initResult] = await bookingInit(
      street,
      houseNumber,
      zipCode,
      city,
      suffix ?? undefined,
      householdId ?? undefined,
      customerId ?? undefined,
    );

    if (initError) {
      notBookableReason.value = initError.data.message;
      return;
    }

    booking.value = initResult;
    addressId.value = initResult.bookingApproach.addressId;
  }

  async function loadHousehold(): Promise<void> {
    if (isNotBookable.value || !params.value?.householdId) {
      return;
    }

    const [getHouseholdsFullDataError, result] = await getHouseholdsFullData(
      params.value.householdId,
      addressId.value!,
    );

    if (getHouseholdsFullDataError) {
      error.value = getHouseholdsFullDataError.data.message;
      return;
    }

    household.value = result;
  }

  return {
    addressId,
    booking,
    error,
    household,
    notBookableReason,
    params,
    hasError,
    initialBookingMode,
    isNotBookable,
    paramsAddress,
    paramsAddressText,
    bookingApproach,
    currentUser,
    phonePrefixes,
    priceList,
    householdIsCompany,
    householdHasProductBooked,
    validateUrlQuery,
    loadBookingMetadata,
    loadHousehold,
  };
});

export default useInitStore;
